import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface PageItem {
  path: string;
  label: string;
  type: 'home' | 'news' | 'case' | 'business' | 'admin' | 'other';
  pv: number;
  uv: number;
}

type TabKey = 'all' | 'front' | 'admin';

// 路径 → 可读名称映射
function getPageLabel(path: string): { label: string; type: PageItem['type'] } {
  if (path === '/') return { label: '首页', type: 'home' };
  if (path.startsWith('/news/')) return { label: `新闻·第${path.replace('/news/', '')}篇`, type: 'news' };
  if (path.startsWith('/case/')) {
    const slug = path.replace('/case/', '');
    const names: Record<string, string> = {
      'smart-city-brain': '智慧城市大脑',
      'manufacturing-ai': '工业AI平台',
      'finance-risk': '金融风控系统',
      'medical-imaging': '医疗影像AI',
      'supply-chain': '供应链优化',
      'content-platform': '内容平台AI',
    };
    return { label: `案例·${names[slug] || slug}`, type: 'case' };
  }
  if (path.startsWith('/business/')) {
    const slug = path.replace('/business/', '');
    const names: Record<string, string> = {
      'computing-power': '算力基础设施',
      'ai-products': 'AI产品矩阵',
      'digital-twin': '数字孪生',
      'data-platform': '数据平台',
      'smart-manufacturing': '智能制造',
      'cloud-native': '云原生服务',
      'edge-computing': '边缘计算',
      'security': '安全服务',
    };
    return { label: `业务·${names[slug] || slug}`, type: 'business' };
  }
  if (path === '/admin') return { label: '后台·仪表盘', type: 'admin' };
  if (path === '/admin/login') return { label: '后台·管理员登录', type: 'admin' };
  if (path === '/admin/cms/news') return { label: '后台·新闻动态管理', type: 'admin' };
  if (path === '/admin/cms/business') return { label: '后台·核心业务管理', type: 'admin' };
  if (path === '/admin/cms/cases') return { label: '后台·客户案例管理', type: 'admin' };
  if (path === '/admin/cms/ai-products') return { label: '后台·AI产品管理', type: 'admin' };
  if (path.startsWith('/admin/cms/')) return { label: `后台·内容管理·${path.replace('/admin/cms/', '')}`, type: 'admin' };
  if (path.startsWith('/admin/')) return { label: `后台·${path.replace('/admin/', '')}`, type: 'admin' };
  return { label: path, type: 'other' };
}

const TYPE_CONFIG: Record<PageItem['type'], { icon: string; color: string; bg: string }> = {
  home:     { icon: 'ri-home-4-line',      color: 'text-teal-600',   bg: 'bg-teal-50' },
  news:     { icon: 'ri-newspaper-line',   color: 'text-sky-600',    bg: 'bg-sky-50' },
  case:     { icon: 'ri-briefcase-line',   color: 'text-violet-600', bg: 'bg-violet-50' },
  business: { icon: 'ri-building-line',    color: 'text-amber-600',  bg: 'bg-amber-50' },
  admin:    { icon: 'ri-settings-3-line',  color: 'text-gray-500',   bg: 'bg-gray-100' },
  other:    { icon: 'ri-pages-line',       color: 'text-gray-400',   bg: 'bg-gray-50' },
};

export default function PageRanking() {
  const [pages, setPages] = useState<PageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('front');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from('site_visits')
          .select('page_path, session_id')
          .gte('created_at', thirtyDaysAgo);

        if (!data || data.length === 0) { setLoading(false); return; }

        // 按路径聚合 PV + UV
        const map: Record<string, { pv: number; sessions: Set<string> }> = {};
        data.forEach((r) => {
          const p = r.page_path || '/';
          if (!map[p]) map[p] = { pv: 0, sessions: new Set() };
          map[p].pv += 1;
          if (r.session_id) map[p].sessions.add(r.session_id);
        });

        const list: PageItem[] = Object.entries(map)
          .map(([path, val]) => {
            const { label, type } = getPageLabel(path);
            return { path, label, type, pv: val.pv, uv: val.sessions.size };
          })
          .sort((a, b) => b.pv - a.pv);

        setPages(list);
      } catch (err) {
        console.error('PageRanking fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'front', label: '前台页面' },
    { key: 'all',   label: '全部（含后台）' },
  ];

  const filtered = pages.filter((p) => {
    if (activeTab === 'front') return p.type !== 'admin';
    return true;
  });

  const maxPV = filtered.length > 0 ? Math.max(...filtered.map((p) => p.pv), 1) : 1;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">页面访问排行</h3>
          <p className="text-xs text-gray-400 mt-0.5">近30天各页面浏览次数 / 访客人数</p>
        </div>
        {/* Tab 切换 */}
        <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 内容 */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-7 h-7 bg-gray-100 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                <div className="h-1.5 bg-gray-100 rounded w-full"></div>
              </div>
              <div className="w-10 h-3 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <i className="ri-pages-line text-3xl mb-2 block"></i>
          <p className="text-xs">暂无访问数据</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.slice(0, 10).map((page, idx) => {
            const cfg = TYPE_CONFIG[page.type];
            return (
              <div key={page.path} className="flex items-center gap-3 group">
                {/* 排名 */}
                <div
                  className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${
                    idx === 0 ? 'bg-amber-400 text-white' :
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-gray-100 text-gray-400'
                  }`}
                >
                  {idx + 1}
                </div>

                {/* 图标 */}
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.bg}`}>
                  <i className={`${cfg.icon} text-sm ${cfg.color}`}></i>
                </div>

                {/* 路径 + 进度条 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700 truncate max-w-[160px]" title={page.label}>
                      {page.label}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs font-bold text-gray-800">{page.pv}</span>
                      <span className="text-xs text-gray-400">浏览</span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-500">{page.uv}</span>
                      <span className="text-xs text-gray-400">访客</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${(page.pv / maxPV) * 100}%`,
                        backgroundColor: idx === 0 ? '#0d9488' : idx < 3 ? '#10b981' : '#d1fae5',
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 汇总 */}
      {!loading && filtered.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-sm font-bold text-gray-800">{filtered.length}</div>
            <div className="text-xs text-gray-400">页面数</div>
          </div>
          <div>
            <div className="text-sm font-bold text-gray-800">
              {filtered.reduce((a, b) => a + b.pv, 0).toLocaleString()}
            </div>
            <div className="text-xs text-gray-400">总浏览次数</div>
          </div>
          <div>
            <div className="text-sm font-bold text-teal-600">
              {filtered.length > 0 ? Math.round(filtered.reduce((a, b) => a + b.pv, 0) / filtered.length) : 0}
            </div>
            <div className="text-xs text-gray-400">均浏览次数</div>
          </div>
        </div>
      )}
    </div>
  );
}
